import classNames from "classnames";
import { AppKind } from "common/components/app-kind";
import { getLocalizedString } from "common/components/localization";
import type { TextEntrySize } from "generator/components/text-entry-types";
import { browserMightBeOniOS } from "lib/browser-detect";
import type { GlideFC } from "lib/glide-fc";
import React from "react";
import { parseNumber } from "common/Support";
import tw from "twin.macro";
import { css } from "webapp/styled-components";
import type { FieldType } from "../text-field/text-field-view-types";

interface Props {
    placeholder?: string;
    dataType?: FieldType;
    dataTypePattern?: string;
    dataTypeInputMode?: "text";
    isEnabled: boolean;
    value: string;
    error?: string;
    onChange?: (newVal: string) => void;
    isRequired?: boolean;
    minimumChars?: number;
    maximumChars?: number;
    size?: TextEntrySize;
    title?: string;
    prefix?: string;
    suffix?: string;
    autoFocus?: boolean;
}

export const WireField: GlideFC<Props> = p => {
    const {
        isEnabled,
        value,
        dataType,
        dataTypePattern,
        dataTypeInputMode,
        onChange,
        placeholder,
        title,
        isRequired,
        error,
        size,
        maximumChars,
        autoFocus,
    } = p;

    const [innerValue, setInnerValue] = React.useState(value);

    const onValueChange = (newValue: string) => {
        setInnerValue(newValue);
        onChange?.(newValue);
    };

    React.useEffect(() => {
        setInnerValue(value);
    }, [value]);

    // The HTML5 spec has left us a pothole regarding number inputs and locales
    // using , as the decimal separator. The string you pass to "value" needs
    // to use . as the decimal separator, because that's the default American-
    // centric locale browsers have always historically used.
    //
    // What this means is that attempting to input a number with , as the
    // decimal separator will look "wrong" to the browser, and as a consequence
    // of the operation happening by way of the shadow DOM in React, when this
    // happens you end up with an empty value landing in the real DOM. And this
    // means your input gets cleared.
    //
    // There is a simple way to cheese this: if it's a number input, always set
    // the current value to an actual number. We have to parse the current
    // state to do so, but this keeps the browser happy.

    return (
        <label tw="flex flex-col text-text-dark">
            <div tw="mb-2 text-sm font-semibold flex justify-between items-baseline">
                <div
                    data-testid="wf-title"
                    tw="flex-shrink text-text-contextual-dark overflow-hidden overflow-ellipsis whitespace-nowrap">
                    {title}
                </div>
                {isRequired && (
                    <div data-testid="wf-required" tw="flex-shrink-0 ml-2 text-xs text-text-xpale font-normal">
                        {getLocalizedString("required", AppKind.Page)}
                    </div>
                )}
            </div>
            {dataType === "text" && (
                <WireGrowingTextArea
                    data-testid="wf-input"
                    className={size}
                    css={css`
                        &.medium {
                            min-height: 76px;
                        }

                        &.large {
                            min-height: 152px;
                        }
                    `}
                    tw="px-3 py-2 bg-bg-front shadow-sm hover:shadow-sm-xdark transition-shadow focus:(ring-accent
                        ring-2) rounded-lg"
                    value={innerValue}
                    disabled={!isEnabled}
                    placeholder={placeholder}
                    onChange={e => onValueChange?.(e.target.value)}
                    autoFocus={autoFocus}
                />
            )}
            {dataType === "number" && (
                <input
                    data-testid="wf-input"
                    tw="disabled:(opacity-50 pointer-events-none) px-3 py-2 bg-bg-front shadow-sm hover:shadow-sm-xdark
                        transition-shadow focus:(ring-accent ring-2) rounded-lg"
                    type="number"
                    pattern={dataTypePattern}
                    inputMode={dataTypeInputMode}
                    value={parseNumber(innerValue)}
                    disabled={!isEnabled}
                    placeholder={placeholder}
                    onChange={e => onValueChange?.(e.target.value)}
                    autoFocus={autoFocus}
                />
            )}
            {dataType !== "text" && dataType !== "number" && (
                <input
                    data-testid="wf-input"
                    tw="disabled:(opacity-50 pointer-events-none) px-3 py-2 bg-bg-front shadow-sm hover:shadow-sm-xdark
                        transition-shadow focus:(ring-accent ring-2) rounded-lg"
                    type={dataType}
                    pattern={dataTypePattern}
                    inputMode={dataTypeInputMode}
                    value={innerValue}
                    disabled={!isEnabled}
                    placeholder={placeholder}
                    onChange={e => onValueChange?.(e.target.value)}
                    autoFocus={autoFocus}
                />
            )}
            <div tw="flex-shrink-0 mt-1 text-xs flex justify-between">
                <div tw="text-text-danger">{error}</div>
                {maximumChars !== undefined && (
                    <div
                        css={css`
                            &.error {
                                ${tw`text-text-danger`};
                            }
                        `}
                        tw="text-text-xpale"
                        className={classNames(value.length > maximumChars && "error")}>
                        {`${value.length}/${maximumChars}`}
                    </div>
                )}
            </div>
        </label>
    );
};

interface WireGrowingEntryProps
    extends React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement> {}

const WireGrowingTextArea = React.forwardRef<HTMLTextAreaElement, WireGrowingEntryProps>((p, ref) => {
    const { placeholder, value, className, autoFocus, ...rest } = p;

    return (
        <div tw="relative all-child:(p-0 m-0 color[inherit] font-size[inherit] font-weight[inherit])">
            <div style={{ padding: browserMightBeOniOS ? "0 3px" : undefined }} tw="w-full invisible">
                <div className={className} tw="whitespace-pre-wrap break-words">
                    {(value ?? placeholder ?? "") + "\n"}
                </div>
            </div>
            <textarea
                className={className}
                tw="appearance-none absolute inset-0 rounded-none resize-none whitespace-pre-wrap w-full h-full
                    overflow-hidden border-0 bg-transparent disabled:(opacity-50 pointer-events-none)"
                ref={ref}
                placeholder={placeholder}
                value={value ?? ""}
                autoFocus={autoFocus}
                {...rest}
            />
        </div>
    );
});
